"use client";

import { ReactNode, useCallback, useEffect, useRef, useState, memo } from "react";
import { Box, type BoxProps, type SxProps, type Theme } from "@mui/material";
import throttle from "lodash.throttle";
import { Shadow } from "./shadow";

interface IScrollShadowIndicatorProps extends Omit<BoxProps, "sx"> {
    threshold?: number;
    children?: ReactNode | null;
    shadowProps?: SxProps<Theme>;
    sx?: SxProps<Theme>;
    px?: number | string | (number | string)[];
}

const THROTTLE_TIME = 200;

const useScrollShadowVisibility = (threshold: number) => {
    const ref = useRef<HTMLDivElement>(null);
    const [shouldShowShadow, setShouldShowShadow] = useState(true);
    const throttledSetShadow = useRef(throttle(setShouldShowShadow, THROTTLE_TIME)).current;

    const handleScroll = useCallback(
        (event: React.UIEvent<HTMLDivElement>) => {
            const element = event.currentTarget;
            const shouldHideShadow = element.scrollTop > threshold || element.scrollHeight === element.clientHeight;
            throttledSetShadow(!shouldHideShadow);
        },
        [threshold, throttledSetShadow]
    );

    const checkIfScrollable = useCallback(() => {
        const element = ref.current;
        if (!element) {
            return;
        }

        const isScrollable = element.scrollHeight > element.offsetHeight;
        setShouldShowShadow(Boolean(isScrollable));
    }, []);

    useEffect(() => {
        checkIfScrollable();

        const resizeObserver = new ResizeObserver(checkIfScrollable);
        if (ref.current) {
            resizeObserver.observe(ref.current);
        }

        window.addEventListener("scroll", checkIfScrollable);
        window.addEventListener("resize", checkIfScrollable);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener("scroll", checkIfScrollable);
            window.removeEventListener("resize", checkIfScrollable);
            throttledSetShadow.cancel();
        };
    }, [checkIfScrollable, throttledSetShadow]);

    return { ref, shouldShowShadow, handleScroll };
};

const ScrollShadowIndicator = memo(function ScrollShadowIndicator({
    threshold = 30,
    children,
    shadowProps,
    sx,
    px,
    ...boxProps
}: IScrollShadowIndicatorProps) {
    const { ref, shouldShowShadow, handleScroll } = useScrollShadowVisibility(threshold);

    return (
        <Box
            sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                ...sx,
            }}
        >
            <Box
                ref={ref}
                onScroll={handleScroll}
                sx={{
                    overflowY: "auto",
                    flex: 1,
                    minHeight: 0,
                    px,
                }}
                {...boxProps}
                aria-label="Scrollable content"
                role="region"
            >
                {children}
            </Box>

            <Shadow shouldShow={shouldShowShadow} shadowProps={shadowProps} />
        </Box>
    );
});

ScrollShadowIndicator.displayName = "ScrollShadowIndicator";

export { ScrollShadowIndicator, type IScrollShadowIndicatorProps };
